import React, { useState, useEffect, useContext } from 'react';
import '../../App.css';
import './compute-service.css';
import CSS from 'csstype';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons
import jupyterHUBIcon from '../../icons/jupyter.380.png';
import cartaIcon from '../../icons/carta-square.380.png';
import canfarIcon from '../../icons/canfar-square.380.png';
import azimuthDarkIcon from '../../icons/azimuth-dark.512.png';
import azimuthLightIcon from '../../icons/azimuth-light.512.png';
import playIcon from '../../icons/play-square.512.png';
import playIconDisabled from '../../icons/play-square-bw.512.png';
import heartIcon from '../../icons/heart.512.png';
import infoIcon from '../../icons/info.512.png';
import warningIcon from '../../icons/warning.512.png';
import renewIcon from '../../icons/refresh.512.png';
import binLightIcon from '../../icons/bin-dark.256.png';
import desktopIcon from '../../icons/virtual-desktop.256.png';
import aladinIcon from '../../icons/aladin-logo.512.png';
import headlessIcon from '../../icons/headless.512.png';
import skyServerIcon from '../../icons/skyserver-logo.512.png';
import unknownDarkIcon from '../../icons/question-mark-dark.256.png';
import unknownLightIcon from '../../icons/question-mark-light.256.png';

// types relating to events.
import { Event, EventType, EventTarget } from '../../utils/events';

// types.
import {
  CanfarServiceType,
  ComputeServiceType,
  ServiceDetails,
  Site,
} from '../../utils/types';

// functions.
import { APIPrefix, getStatusClass } from '../../utils/functions';
import { handleKeyboardActivation } from '../../utils/accessibility';

// components.
import VisualisationAladin from '../interactive-sessions/visualisation-aladin';
import VisualisationSkyServer from '../interactive-sessions/visualisation-skyserver';
import PleaseWait from '../../tools/please-wait';
import SiteIcon from '../../tools/site-icon';
import { siteName } from '../../utils/functions';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  addTab,
  setSessionOverriddenStatus,
  setSessionRefreshing,
} from '../interactive-sessions/interactive-sessions-slice';
import {
  displaySession,
  refreshCanfarServices,
  refreshSRCNetServices,
} from '../interactive-sessions/interactive-sessions-actions';
import { setShowNewSessionModal } from '../../app-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

function SessionCardContent(props: { session: ComputeServiceType }) {
  //
  let status: string = props.session.compute_status;
  if (props.session.overridden_status !== undefined) {
    status = props.session.overridden_status;
  }
  const showSpinner: boolean =
    status === 'removing' || status === 'starting' || status === 'renewing';

  const serviceName: string =
    props.session.service_name !== ''
      ? props.session.service_name
      : 'Unnamed ' + props.session.session_type?.toUpperCase() + ' service';

  const getServiceIcon = (type: string): any => {
    const typeLC = type?.toLowerCase() ?? '';
    if (typeLC === 'jupyterhub' || typeLC === 'notebook') {
      return jupyterHUBIcon;
    }
    if (typeLC === 'canfar') {
      return canfarIcon;
    }
    if (typeLC === 'carta') {
      return cartaIcon;
    }
    if (typeLC === 'desktop') {
      return desktopIcon;
    }
    if (typeLC === 'aladinlite') {
      return aladinIcon;
    }
    if (typeLC === 'skyserver') {
      return skyServerIcon;
    }
    if (typeLC === 'headless') {
      return headlessIcon;
    }

    return unknownDarkIcon;
    //
  };

  //	------------------------------------------------------------
  //
  //	format a date that is in text format.
  //
  //	------------------------------------------------------------

  function formatDate(args: { value: string }) {
    //
    let formattedDateTime: string = args.value;

    if (formattedDateTime !== '/') {
      let date: Date = new Date(args.value);
      formattedDateTime = date.toLocaleString();
    }

    // return something.
    return formattedDateTime;
    //
  } // formatDate

  return (
    <>
      {showSpinner && (
        <div className="session-spinner-overlay">
          <div className="spinner" />
        </div>
      )}
      <div className="session-header">
        <div className="session-icon">
          <img
            src={getServiceIcon(props.session.session_type)}
            alt={props.session.session_type}
          />
        </div>
        <div className="session-name">{serviceName}</div>
        {props.session.health_status !== undefined && (
          <TrafficLights healthStatus={props.session.health_status} />
        )}
      </div>

      {/*(status === 'running' ||
        status === 'pending' ||
        status === 'starting' ||
        status === 'removing' ||
        status === 'renewing') && (*/}
      <div className="session-info">
        <div className="session-status-container">
          <div
            className={`status-badge ${getStatusClass({ status: status.toUpperCase() })}`}
          >
            {status.toUpperCase()}
          </div>
        </div>
        <div className="session-detail">
          expires: {formatDate({ value: props.session.expires_at ?? '/' })}
        </div>
      </div>
      {/*)}*/}
    </>
  );
  //
} // SessionCardContent

//	------------------------------------------------------------
//
//	this column holds the traffic lights indicating the service
//	health.
//
//	------------------------------------------------------------

function TrafficLights(args: { healthStatus: string }) {
  //
  return (
    <div
      role="region"
      aria-label="Service health status"
      style={{
        flex: '0 0 20px',
        display: 'flex',
        height: '68px',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        className="search-compute-gray-border"
        style={{
          flex: '0 0 47px',
          width: '16px',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          borderBottom: 'none',
          borderRadius: '5px 5px 0px 0px',
          position: 'absolute',
        }}
      >
        <div style={{ flex: '0 0 1px' }}></div>
        <div
          className="search-compute-service-light"
          style={{
            flex: '0 0 12px',
            borderRadius: '6px',
            width: '12px',
            fontSize: '7pt',
          }}
          data-light="green"
          data-status={args.healthStatus.toLowerCase()}
        >
          ✔
        </div>
        <div style={{ flex: '0 0 1px' }}></div>
        <div
          className="search-compute-service-light"
          style={{
            flex: '0 0 12px',
            borderRadius: '6px',
            width: '12px',
            fontSize: '7pt',
          }}
          data-light="orange"
          data-status={args.healthStatus.toLowerCase()}
        >
          ?
        </div>
        <div style={{ flex: '0 0 1px' }}></div>
        <div
          className="search-compute-service-light"
          style={{
            flex: '0 0 12px',
            borderRadius: '6px',
            width: '12px',
            fontSize: '7pt',
          }}
          data-light="red"
          data-status={args.healthStatus.toLowerCase()}
        >
          ✘
        </div>
        <div style={{ flex: '0 0 6px' }}></div>
      </div>
      <div
        className="search-compute-gray-border"
        style={{
          flex: '0 0 20px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '2px 0px 0px 2px',
          borderRadius: '10px',
          position: 'relative',
          top: '47px',
        }}
      >
        <img
          src={heartIcon}
          alt="Health status"
          width="14px"
          height="14px"
          style={{ backgroundColor: 'transparent' }}
        />
      </div>
    </div>
  );
  //
} // TrafficLights

//	------------------------------------------------------------
//
//	Display a tile with an active or available compute session.
//
//	------------------------------------------------------------

export default function ComputeSession(props: { session: ComputeServiceType }) {
  //
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sD_canfarServices: CanfarServiceType[] = useAppSelector(
    (state) => state.data.canfarServices ?? [],
  );
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const [loading, setLoading] = useState<boolean>(false);

  const canfarService: CanfarServiceType | undefined = sD_canfarServices.find(
    (service) => service.site.site_uuid === props.session.site.site_uuid,
  );

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Displays the bottom banner depending on the banner type.
  //
  //	------------------------------------------------------------

  function bottomBanner(args: { type: string | undefined; value: string | undefined }) {
    //
    // extract the date part from the first set of curly braces.
    function extractDate(value: string) {
      //
      // remove {} from the value.
      const startBrace = value.indexOf('{');
      const endBrace = value.indexOf('}');
      let dateValue: string = '';
      let remainder: string = value;
      if (startBrace > -1 && endBrace > -1 && endBrace > startBrace) {
        dateValue = value.substring(startBrace + 1, endBrace);
        remainder = value.substring(endBrace + 1, value.length);
      }
      if (startBrace === -1 && endBrace === -1) {
        dateValue = value;
      }

      return {
        dateValue: dateValue,
        remainder: remainder,
      };
      //
    } // extractDate

    var banner: string = args.value ?? '';

    if (args.type === 'until') {
      //
      // extract date.
      const toValue: {
        dateValue: string;
        remainder: string;
      } = extractDate(banner);

      const toDateTime: Date = new Date(toValue.dateValue);
      banner = 'until ' + toDateTime.toLocaleString();
    }

    if (args.type === 'from_to') {
      //
      // get the from date.
      const fromValue: {
        dateValue: string;
        remainder: string;
      } = extractDate(banner);

      // get the to date.
      const toValue: {
        dateValue: string;
        remainder: string;
      } = extractDate(fromValue.remainder);

      // convert to datetimes.
      const fromDateTime: Date = new Date(fromValue.dateValue);
      const toDateTime: Date = new Date(toValue.dateValue);

      // build banner.
      banner =
        'maintenance: ' +
        fromDateTime.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        }) +
        ' to ' +
        toDateTime.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });
      //
    }

    return banner;
    //
  } // bottomBanner

  //	-------------------------------------------------
  //
  //	function that computes duration in days
  //
  //	-------------------------------------------------

  const computeSessionDuration = (): number => {
    //
    if (
      props.session.started_at !== undefined &&
      props.session.expires_at !== undefined
    ) {
      //
      const start_date = new Date(props.session.started_at);
      const end_date = new Date(props.session.expires_at);

      const diff = Math.floor(
        (end_date.getTime() - start_date.getTime()) / (1000 * 60 * 60 * 24),
      );
      const duration = Math.max(2, Math.round(diff));

      return duration;
      //
    } else {
      return -1;
    }
    //
  }; // computeSessionDuration

  //	------------------------------------------------------------
  //
  //	Create a new compute session in the DB for a
  //	SRCNet compute session.
  //
  //	------------------------------------------------------------

  async function createNewSession(args: { service: ComputeServiceType }) {
    //
    if (args.service.resource_type === 'canfar') {
      dispatch(
        setShowNewSessionModal({
          show: true,
          site_uuid: args.service.site.site_uuid,
          service_type: args.service.session_type,
        }),
      );
    } else if (args.service.resource_type === 'srcnet') {
      dispatch(
        setShowNewSessionModal({
          show: true,
          site_uuid: args.service.site.site_uuid,
          service_uuid: args.service.compute_service_id,
        }),
      );
    }
    //
  } // createNewSession

  //	------------------------------------------------------------
  //
  //	Handler for DIV clicks.
  //
  //	------------------------------------------------------------

  const handleSessionActivation = () => {
    //
    // open a running service, either in a new browser tab or a new tab on the page.
    if (['running'].indexOf(props.session.compute_status) > -1) {
      displaySession({ session: props.session });
    }

    // start an available service.
    if (
      ['canfar', 'srcnet'].indexOf(props.session.resource_type ?? '#') > -1 &&
      ['jupyterhub', 'notebook', 'desktop', 'carta', 'headless'].indexOf(
        props.session.session_type,
      ) > -1 &&
      props.session.compute_status === 'available'
    ) {
      createNewSession({ service: props.session });
    }

    // open a canfar and azimuth service without displaying the dialog.
    if (
      (props.session.resource_type ?? '#') === 'srcnet' &&
      ['canfar', 'azimuth'].indexOf(props.session.session_type) > -1 &&
      props.session.compute_status === 'available'
    ) {
      displaySession({ session: props.session });
    }

    // open a web resource in a new tab on the page.
    if (props.session.session_type === 'aladinlite') {
      dispatch(
        addTab({
          service: props.session,
          toolComponent: VisualisationAladin,
          index: 0,
        }),
      );
    }
    if (props.session.session_type === 'skyserver') {
      dispatch(
        addTab({
          service: props.session,
          toolComponent: VisualisationSkyServer,
          index: 0,
        }),
      );
    }
    //
  }; // handleSessionActivation

  //	-------------------------------------------------
  //
  //	Handler for btn clicks.
  //
  //	-------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // if the renew session button is clicked.
    if (button.name === 'btn-renew') {
      event.stopPropagation();
      renewComputeSession({ duration: computeSessionDuration() });
    }

    // if the remove session button is clicked.
    if (button.name === 'btn-remove') {
      event.stopPropagation();
      removeComputeSession();
    }

    // handle a click on the whole element.
    if (button.name === '') {
      handleSessionActivation();
    }
    //
  }; // onClickBtnHandler

  //-------------------------------------------------
  //
  //  Remove an active session
  //
  //-------------------------------------------------

  async function removeComputeSession() {
    //
    // renew a SRCNet session.
    if (props.session.resource_type === 'srcnet') {
      //
      // update the status of the compute session, to set the overriden status to 'removing'.
      dispatch(
        setSessionOverriddenStatus({ sessionID: props.session.id, status: 'removing' }),
      );

      try {
        //
        const response = await fetch(
          APIPrefix() +
            `/v1/active_compute_sessions/remove?compute_session_id=${props.session.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate deletion
        await new Promise((resolve) => setTimeout(resolve, 300));

        const result = await response.json();
        if (!response.ok) {
          //
          throw new Error(result.message || 'Failed to remove session');

          // remove the temporary status of the compute session.
          dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));
          //
        } else {
          //
          // Refetch sessions.
          dispatch(refreshSRCNetServices());
          //
        }
        //
      } catch (err) {
        //
        console.error('Error removing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));
        //
      }
      //
    }

    // remove a CANFAR session.
    if (props.session.resource_type === 'canfar' && canfarService !== undefined) {
      //
      // update the status of the compute session, to set the overriden status to 'removing'.
      dispatch(
        setSessionOverriddenStatus({
          computeServiceID: props.session.compute_service_id,
          status: 'removing',
        }),
      );

      try {
        //
        const response = await fetch(
          APIPrefix() +
            `/v1/skaha/session/${props.session.compute_service_id}?url=${encodeURIComponent(canfarService.url)}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate renewal
        await new Promise((resolve) => setTimeout(resolve, 300));

        //const result = await response.json();
        if (response.status === 200) {
          //
          // Refetch sessions.
          dispatch(
            refreshCanfarServices({
              canfarService: canfarService,
              refreshAvailable: false,
            }),
          );
          //
        } else {
          //
          throw new Error('Failed to remove session');

          // remove the temporary status of the compute session.
          dispatch(
            setSessionOverriddenStatus({
              computeServiceID: props.session.compute_service_id,
            }),
          );
          //
        }
        //
      } catch (err) {
        //
        console.error('Error removing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(
          setSessionOverriddenStatus({
            computeServiceID: props.session.compute_service_id,
          }),
        );
        //
      }
      //
    }
    //
  } // removeComputeSession

  //-------------------------------------------------
  //
  //  Renew active session
  //
  //-------------------------------------------------

  async function renewComputeSession(args: { duration: number }) {
    //
    // renew a SRCNet session.
    if (props.session.resource_type === 'srcnet') {
      //
      // update the status of the compute session, to set the overriden status to 'renewing'.
      dispatch(
        setSessionOverriddenStatus({ sessionID: props.session.id, status: 'renewing' }),
      );

      try {
        //
        const response = await fetch(
          APIPrefix() +
            `/v1/active_compute_sessions/renew?compute_session_id=${props.session.id}&duration=${args.duration}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate renewal
        await new Promise((resolve) => setTimeout(resolve, 300));

        const result = await response.json();
        if (!response.ok) {
          //
          throw new Error(result.message || 'Failed to renew session');

          // remove the temporary status of the compute session.
          dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));
          //
        } else {
          //
          // Refetch sessions.
          dispatch(refreshSRCNetServices());
          //
        }
        //
      } catch (err) {
        //
        console.error('Error renewing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));
        //
      }
      //
    }

    // renew a CANFAR session.
    if (props.session.resource_type === 'canfar' && canfarService !== undefined) {
      //
      // update the status of the compute session, to set the overriden status to 'renewing'.
      dispatch(
        setSessionOverriddenStatus({
          computeServiceID: props.session.compute_service_id,
          status: 'renewing',
        }),
      );

      try {
        //
        const response = await fetch(
          APIPrefix() +
            `/v1/skaha/session/${props.session.compute_service_id}/renew?url=${encodeURIComponent(canfarService.url)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate renewal
        await new Promise((resolve) => setTimeout(resolve, 300));

        //const result = await response.json();
        if (response.status === 200) {
          //
          // Refetch sessions.
          dispatch(
            refreshCanfarServices({
              canfarService: canfarService,
              refreshAvailable: false,
            }),
          );
          //
        } else {
          //
          throw new Error('Failed to renew session');

          // remove the temporary status of the compute session.
          dispatch(
            setSessionOverriddenStatus({
              computeServiceID: props.session.compute_service_id,
            }),
          );
          //
        }
        //
      } catch (err) {
        //
        console.error('Error renewing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(
          setSessionOverriddenStatus({
            computeServiceID: props.session.compute_service_id,
          }),
        );
        //
      }
      //
    }
    //
  } // renewComputeSession

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  const isRunning = props.session.compute_status === 'running';
  const isStartable =
    ['canfar', 'srcnet'].indexOf(props.session.resource_type ?? '#') > -1 &&
    props.session.compute_status === 'available';
  const isWebResource =
    ['aladinlite', 'skyserver'].indexOf(
      props.session.session_type?.toLowerCase() ?? '#',
    ) > -1;
  const isActionable = isRunning || isStartable || isWebResource;

  return (
    <button
      aria-label={'Launch compute service'}
      style={{
        margin: '8px 10px 8px 12px',
        position: 'relative',
        background: 'white',
        borderRadius: '10px',
        padding: '0px',
        width: '275px',
        border: '1pt solid #c0c0c0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: '0 0 auto',
        float: 'left',
      }}
      onClick={onClickBtnHandler}
      disabled={!isActionable}
    >
      {(props.session.resource_type === 'srcnet' ||
        props.session.resource_type === 'canfar') && (
        <div
          className={[
            'status-banner',
            props.session.active_status === true ? 'noissuesreported' : 'disabled',
          ].join(' ')}
          style={{
            height: 'auto',
            width: '100%',
            padding: '2px 5px 2px 5px',
            display: 'flex',
            justifyContent: 'right',
            minHeight: '24px',
            margin: '0px 0px 2px 0px',
          }}
        >
          {props.session.top_banner?.toUpperCase() ?? ''}
        </div>
      )}
      <div style={{ position: 'relative', flex: '1 1' }}>
        <div
          style={{
            backgroundColor: 'green',
            position: 'absolute',
            left: '0px',
            top: '0px',
            width: '25px',
            height: '100%',
          }}
        >
          <div
            style={{
              color: 'white',
              fontSize: '12px',
              transform: 'rotate(90deg)',
              textAlign: 'left',
              margin: '5px',
            }}
          >
            {props.session.resource_type}
          </div>
        </div>
        <div
          style={{
            textDecoration: 'none',
            color: 'inherit',
            cursor:
              [
                'notebook',
                'jupyterhub',
                'desktop',
                'carta',
                'canfar',
                'azimuth',
                'headless',
              ].indexOf(props.session.session_type) > -1
                ? 'pointer'
                : 'default',
            flex: '1 1',
            padding:
              '5px 30px ' +
              (props.session.compute_status === 'running' ||
              props.session.resource_type === 'srcnet' ||
              props.session.resource_type === 'canfar'
                ? '0px'
                : '5px') +
              ' 30px',
          }}
        >
          <SessionCardContent session={props.session} />
        </div>
        <div
          style={{
            /*flex: '0 0 auto',*/
            /*margin: '5px 2px 2px 0px',*/
            /*display: 'flex',*/
            /*flexDirection: 'row',*/
            /*width: '100%',
            /*justifyContent: 'right',*/
            backgroundColor: 'green',
            position: 'absolute',
            left: '247px',
            top: '0px',
            width: '25px',
            height: '100%',
            /*textAlign: 'left',*/
            justifyContent: 'end',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              flex: '1 1',
            }}
          >
            <div
              className="text-colour"
              style={{
                /*flex: '1 1',*/
                /*margin: '0px 5px 0px 26px',*/
                /*display: 'flex',*/
                /*justifyContent: 'right',*/
                position: 'absolute',
                left: '-32px',
                top: '34px',
                transform: 'rotate(-90deg)',
                textAlign: 'left',
                fontSize: '8pt',
                /*justifyContent: 'right',*/
                height: '25px',
                width: '90px',
                /*margin: '5px',*/
                padding: '4px 0px 0px 2px',
              }}
            >
              {siteName({ site: props.session.site }).slice(0, 14)}
            </div>
          </div>
          <div
            style={{
              margin: '0px 2px 0px 2px',
            }}
          >
            <SiteIcon site={props.session.site} size={21} />
          </div>
          <div style={{ flex: '0 0 2px' }} />
        </div>
      </div>
      {(props.session.compute_status === 'running' ||
        props.session.resource_type === 'srcnet' ||
        props.session.resource_type === 'canfar') && (
        <div
          style={{
            background: '#252858',
            padding: '0px 4px 0px 4px',
            margin: '2px 0px 0px 0px',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            minHeight: '42px',
          }}
        >
          <PleaseWait
            text="refreshing....."
            visible={props.session.refreshing === true}
          />
          {props.session.bottom_banner_type !== undefined &&
            props.session.refreshing !== true && (
              <div
                style={{
                  flex: '0 0 26px',
                  padding: '0px 0px 0px 4px',
                  display:
                    props.session.bottom_banner_type === 'none' ? 'none' : 'inline',
                }}
              >
                {props.session.active_status === false ? (
                  <img
                    src={warningIcon}
                    alt="Warning message"
                    width="18px"
                    height="18px"
                  />
                ) : (
                  <img
                    src={infoIcon}
                    alt="Information message"
                    width="18px"
                    height="18px"
                  />
                )}
              </div>
            )}
          <div style={{ flex: '1 1', color: 'white' }}>
            {props.session.refreshing !== true &&
              bottomBanner({
                type: props.session.bottom_banner_type,
                value: props.session.bottom_banner,
              })}
          </div>
          {['srcnet', 'canfar'].indexOf(props.session.resource_type ?? '#') > -1 &&
          props.session.compute_status === 'running' &&
          props.session.overridden_status === undefined ? (
            <button
              name="btn-renew"
              title="Renew session"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0px',
                width: '30px',
                height: '30px',
              }}
              onClick={onClickBtnHandler}
            >
              <img style={{ width: '24px', height: '24px' }} src={renewIcon} alt="icon" />
            </button>
          ) : props.session.compute_status === 'available' ? (
            <></>
          ) : (
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0px',
                width: '30px',
                height: '30px',
              }}
              disabled
            >
              <img style={{ width: '24px', height: '24px' }} src={renewIcon} alt="icon" />
            </button>
          )}
          {['srcnet', 'canfar'].indexOf(props.session.resource_type ?? '#') > -1 &&
          props.session.compute_status === 'running' &&
          props.session.overridden_status === undefined ? (
            <button
              name="btn-remove"
              title="Remove session"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0px',
                width: '30px',
                height: '30px',
              }}
              onClick={onClickBtnHandler}
            >
              <img
                style={{ width: '24px', height: '24px' }}
                src={binLightIcon}
                alt="icon"
              />
            </button>
          ) : props.session.compute_status === 'available' ? (
            <></>
          ) : (
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0px',
                width: '30px',
                height: '30px',
              }}
              disabled
            >
              <img
                style={{ width: '24px', height: '24px' }}
                src={binLightIcon}
                alt="icon"
              />
            </button>
          )}
        </div>
      )}
    </button>
  );
  //
} // ComputeSession

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------
